/**
 * `CaseChip` — which case a run belongs to.
 *
 * The Agents pane is CROSS-CASE by design (`caseId: null` on both
 * pollers), and the realistic working pattern is to spawn long-running
 * agents in one case, switch to another, spawn more there, and repeat.
 * Without this, a pane full of "Reprocessar fontes…" rows is ambiguous
 * exactly when it matters most — the lawyer cannot tell which matter a
 * given run is touching, and two cases' work looks identical.
 *
 * Shown on EVERY case-bound row, including runs from the case currently
 * on screen. Labelling only the "other" cases would make the absence of
 * a chip carry meaning, which is a subtler thing to read than just
 * saying it every time.
 *
 * SILENT WHEN THERE IS NO CASE TO NAME (operator directive 2026-07-19):
 * a run either shows its case or shows nothing. Two situations reach that
 * silence and they are NOT the same thing:
 *
 *   1. the run has no case binding at all — org-scoped harnesses and
 *      `system` scheduled jobs are org-wide BY DESIGN; nothing is wrong;
 *   2. the run carries a caseId that does not resolve — a genuine data
 *      fault (a mission holding a folder name, or a registry row missing
 *      the slug the D-4 bridge needs).
 *
 * The lawyer-facing pane treats both as "no case to show", because an
 * internal integrity problem is not the lawyer's to triage. Case (2) is
 * still NOT swallowed: it emits a one-time console warning and keeps a
 * `data-case-unresolved` marker for tests, so the fault stays detectable
 * by engineering without being shouted at the user. Silence in the UI is
 * a presentation decision here — it must never become silence in the
 * diagnostics. See `case-label.ts` on why a plausible-looking substitute
 * label is never acceptable.
 */
import { useTranslation } from "react-i18next";
import { Folder } from "lucide-react";

import { cn } from "@/lib/utils";

export interface CaseChipProps {
  /** Resolved case title, when the case registry knows this id. */
  label?: string;
  /** The raw caseId — used for the tooltip and the unresolved fallback. */
  caseId: string;
  /** True when this run belongs to the case the lawyer is looking at. */
  isActiveCase?: boolean;
  /** False until the case registry has actually loaded. Without it the
   *  unresolved-warning fires on first paint for perfectly valid ids,
   *  simply because the index is still empty — a false alarm that would
   *  quickly train everyone to ignore the real one. */
  registryLoaded?: boolean;
  testId?: string;
}

/** caseIds already reported, so a re-render / poll tick does not spam. */
const warnedCaseIds = new Set<string>();

export function CaseChip({
  label,
  caseId,
  isActiveCase,
  registryLoaded,
  testId,
}: CaseChipProps) {
  const { t } = useTranslation();

  // A caseId that is PRESENT but unresolvable is an anomaly worth knowing
  // about even though the pane stays quiet — warn once per id, and only
  // once the registry is actually loaded (see `registryLoaded`).
  if (registryLoaded && !label && caseId && !warnedCaseIds.has(caseId)) {
    warnedCaseIds.add(caseId);
    console.warn(
      `[team-of-agents] run references caseId "${caseId}" which is not in the ` +
        `case registry — the row will render without a case. This usually means ` +
        `the run stored a folder segment instead of a caseId, or the registry ` +
        `row is missing its slug (see ai-docs/handoffs/case-identity-desync-relay-2026-07-19.md).`,
    );
  }

  // Nothing to name → render nothing. The marker element is kept
  // zero-size (not omitted) ONLY when a caseId was present but failed to
  // resolve, so automated checks can still see the fault the UI hides.
  if (!label) {
    return caseId ? (
      <span data-case-id={caseId} data-case-unresolved="true" hidden />
    ) : null;
  }

  return (
    <span
      data-testid={testId}
      data-case-id={caseId}
      title={t("rightSidebar.caseChipTitle", { title: label })}
      className={cn(
        // Deliberately quiet: the case is context, not the row's headline.
        // No pill background and a muted foreground, so it reads as a label
        // rather than a link competing with the title. The active case is
        // distinguished by weight alone, not a vivid colour block.
        "inline-flex min-w-0 max-w-full shrink items-center gap-0.5 text-[9px]",
        // Even quieter than the title: the case is background context. The
        // active case gets a hair more contrast, nothing more.
        isActiveCase
          ? "font-normal text-slate-400 dark:text-slate-500"
          : "font-normal text-slate-400/70 dark:text-slate-500/70",
      )}
    >
      <Folder size={8} className="shrink-0 opacity-70" aria-hidden />
      <span className="truncate">{label}</span>
    </span>
  );
}
